import { DEFAULT_PERSONA, POLICY_IDS, policyName } from '@/features/decisions/config/personaContext';
import type { FcfEntitlementBar } from '@/features/decisions/decisions.api.types';
import { fmtMoneyM } from '@/features/decisions/utils/fcfFormat';
import { computeOcfDeltas } from '@/features/decisions/utils/ocf';

// ECharts option builder for the Incremental Operating Cash Flow by Policy chart.
// Reference bar = 0 baseline; each comparison bar = Δ Operating Cash Flow vs the
// reference at the shared lost-sales assumption — the SAME computeOcfDeltas the
// Operating Cash Flow Build Up uses, so the bars and the build-up reconcile.
export function buildFcfChartOption(
  refBar: FcfEntitlementBar | undefined,
  cmpBars: FcfEntitlementBar[],
  lostSalesPct: number,
  keystoneOnlyOverride: boolean = DEFAULT_PERSONA.keystoneOnlyOverride,
  overrideValue: number = DEFAULT_PERSONA.overrideValue,
  chartMultiplier: number = DEFAULT_PERSONA.chartMultiplier,
) {
  const bars = refBar ? [refBar, ...cmpBars] : cmpBars;
  if (bars.length === 0) {
    return {
      title: { text: 'No data', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 14 } },
      xAxis: { show: false },
      yAxis: { show: false },
    };
  }

  // Reference vs itself → 0 baseline; each comparison → its Δ Operating Cash Flow
  // vs reference at the shared lost-sales assumption. Falls back to 0 if no reference.
  const values: number[] = bars.map((b) => {
    if (keystoneOnlyOverride && b.experiment_id?.toLowerCase() === POLICY_IDS.keystoneForecast.toLowerCase()) {
      return overrideValue * chartMultiplier;
    }
    return (refBar ? (computeOcfDeltas(refBar, b, lostSalesPct).ocf ?? 0) : 0) * chartMultiplier;
  });
  // x-axis labels are hidden (rendered by the policy header below the chart, Max
  // review); categories keep the policy name for the axis tooltip only.
  const categories: string[] = bars.map((b) => policyName(b.experiment_id, b.is_reference));
  const colors = ['#cbd5e1', '#34d399', '#047857', '#065f46'];

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (v: unknown) => (typeof v === 'number' ? fmtMoneyM(v, 2) : '—'),
    },
    legend: { show: false },
    // No x-labels (policy header below) and no y-title (repetitive) — Max review.
    grid: { top: 40, right: 24, bottom: 20, left: 68 },
    xAxis: {
      type: 'category',
      data: categories,
      axisLine: { lineStyle: { color: '#cbd5e1' } },
      axisTick: { show: false },
      // Rendered by the policy header below the chart; stated once (Max review).
      axisLabel: { show: false },
    },
    yAxis: {
      type: 'value',
      // y-axis title killed (repetitive with the chart title); ticks enlarged.
      axisLabel: { fontSize: 18, color: '#64748b', formatter: (v: number) => fmtMoneyM(v, 0) },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    series: [
      {
        type: 'bar',
        barWidth: 96,
        data: values.map((v, i) => ({
          value: v,
          itemStyle: { color: colors[i] ?? colors[colors.length - 1] },
        })),
        label: {
          show: true,
          position: 'top',
          fontSize: 16,
          fontWeight: 'bold',
          color: '#0f172a',
          formatter: (params: { dataIndex: number; value: number }) =>
            params.dataIndex === 0 ? `${fmtMoneyM(values[0], 0)} (baseline)` : fmtMoneyM(params.value, 1),
        },
      },
    ],
  };
}
